
import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Sword, Coins, Skull, Info, Flag, Circle, Lock, X, Flame, Ghost, ShieldAlert, Gift
} from 'lucide-react';
import { Dungeon, Room, RoomType, Connection, EncounterType, CustomEncounter } from '../types';
import { createConnection, PRNG } from '../utils/dungeonGenerator';

interface DungeonCanvasProps {
  dungeon: Dungeon;
  setDungeon: (d: Dungeon) => void;
  isMasterMode: boolean;
  onEditRoom: (id: string) => void;
  onEditSegment: (connId: string, segId: string) => void;
  onToggleRoom: (id: string) => void;
  onToggleSegment: (connId: string, segId: string) => void;
  customEncounters: CustomEncounter[];
}

const CELL_SIZE = 60;

const GenericIcon = ({ char, size = 18 }: { char: string, size?: number }) => {
  // Se o input for um código hex (ex: f523), renderiza via Font Awesome
  const isHex = /^[0-9a-fA-F]{4}$/.test(char);

  if (isHex) {
    return (
      <i
        className="fa-solid font-fa flex items-center justify-center leading-none select-none"
        style={{ fontSize: size }}
      >
        {String.fromCharCode(parseInt(char, 16))}
      </i>
    );
  }

  return (
    <div style={{ fontSize: size }} className="font-bold flex items-center justify-center leading-none select-none">
      {char}
    </div>
  );
};

const DungeonCanvas: React.FC<DungeonCanvasProps> = ({ dungeon, setDungeon, isMasterMode, onEditRoom, onEditSegment, onToggleRoom, onToggleSegment, customEncounters }) => {
  const [viewState, setViewState] = useState({ x: 200, y: window.innerHeight / 2, scale: 0.8 });
  const [isPanning, setIsPanning] = useState(false);
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const [hoveredElement, setHoveredElement] = useState<{ x: number, y: number, content: string } | null>(null);
  const [draggingRoomId, setDraggingRoomId] = useState<string | null>(null);
  const [linkSource, setLinkSource] = useState<{ id: string, type: 'room' | 'segment', connId?: string, gridX?: number, gridY?: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const theme = dungeon.theme;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Shift') setIsShiftPressed(true); };
    const handleKeyUp = (e: KeyboardEvent) => { if (e.key === 'Shift') setIsShiftPressed(false); };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    const zoomSpeed = 0.0015;
    const delta = -e.deltaY * zoomSpeed;
    const newScale = Math.min(Math.max(viewState.scale + delta, 0.15), 4);
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const dx = (mouseX - viewState.x) / viewState.scale;
    const dy = (mouseY - viewState.y) / viewState.scale;
    setViewState(prev => ({ scale: newScale, x: mouseX - dx * newScale, y: mouseY - dy * newScale }));
  }, [viewState]);

  const handlePointerDown = (e: React.PointerEvent) => { if (e.button === 0) setIsPanning(true); };
  const handlePointerMove = useCallback((e: PointerEvent) => {
    if (isPanning) {
      setViewState(prev => ({ ...prev, x: prev.x + e.movementX, y: prev.y + e.movementY }));
    } else if (draggingRoomId) {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const mouseX = e.clientX - rect.left - viewState.x;
      const mouseY = e.clientY - rect.top - viewState.y;
      const gridX = Math.round(mouseX / (viewState.scale * CELL_SIZE));
      const gridY = Math.round(mouseY / (viewState.scale * CELL_SIZE));

      setDungeon(prev => ({
        ...prev,
        rooms: prev.rooms.map(r => r.id === draggingRoomId ? { ...r, gridX, gridY } : r)
      }));
    }
  }, [isPanning, draggingRoomId, viewState]);

  const handlePointerUp = useCallback(() => {
    if (draggingRoomId && dungeon) {
      const movedRoom = dungeon.rooms.find(r => r.id === draggingRoomId);
      if (movedRoom) {
        const updatedConnections = dungeon.connections.map(conn => {
          if (conn.fromId === draggingRoomId || conn.toId === draggingRoomId) {
            const from = dungeon.rooms.find(r => r.id === conn.fromId);
            const to = dungeon.rooms.find(r => r.id === conn.toId);
            if (from && to) {
              const rng = new PRNG(Date.now().toString());
              return createConnection(from, to, rng, dungeon.rooms);
            }
          }
          return conn;
        });
        setDungeon({ ...dungeon, connections: updatedConnections });
      }
    }
    setIsPanning(false);
    setDraggingRoomId(null);
  }, [draggingRoomId, dungeon]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const renderRoomIcon = (type: RoomType) => {
    const custom = theme.roomIcons?.[type];
    if (custom) return <GenericIcon char={custom} size={42} />;
    switch (type) {
      case 'START': return <Flag size={42} fill="currentColor" />;
      case 'BATTLE': return <Sword size={42} fill="currentColor" />;
      case 'TREASURE': return <Coins size={42} fill="currentColor" />;
      case 'BOSS': return <Skull size={48} fill="currentColor" />;
      case 'CAMP': return <Flame size={42} fill="currentColor" />;
      case 'CURIO': return <Info size={42} fill="currentColor" />;
      case 'JUNCTION': return null;
      default: return <Circle size={14} fill="currentColor" />;
    }
  };

  const handleCreateBifurcation = useCallback((source: { id: string, type: 'room' | 'segment', connId?: string, gridX?: number, gridY?: number }, target: { id: string, type: 'room' | 'segment', connId?: string, gridX?: number, gridY?: number }) => {
    const segmentSource = source.type === 'segment' ? source : (target.type === 'segment' ? target : null);
    const roomTarget = source.type === 'room' ? source : (target.type === 'room' ? target : null);

    if (segmentSource && roomTarget && segmentSource.connId) {
      const junctionId = `junction-${Date.now()}`;
      const junctionRoom: Room = {
        id: junctionId,
        type: 'JUNCTION',
        gridX: segmentSource.gridX!,
        gridY: segmentSource.gridY!,
        label: '', // Sem nome
        revealed: true,
        enemies: [],
        description: '' // Sem descrição
      };

      const oldConn = dungeon.connections.find(c => `${c.fromId}-${c.toId}` === segmentSource.connId);
      if (oldConn) {
        const fromRoom = dungeon.rooms.find(r => r.id === oldConn.fromId);
        const toRoom = dungeon.rooms.find(r => r.id === oldConn.toId);
        const targetRoom = dungeon.rooms.find(r => r.id === roomTarget.id);

        if (fromRoom && toRoom && targetRoom) {
          const rng = new PRNG(Date.now().toString());
          const conn1 = createConnection(fromRoom, junctionRoom, rng, dungeon.rooms);
          const conn2 = createConnection(junctionRoom, toRoom, rng, dungeon.rooms);
          const conn3 = createConnection(junctionRoom, targetRoom, rng, dungeon.rooms);

          setDungeon({
            ...dungeon,
            rooms: [...dungeon.rooms, junctionRoom],
            connections: [
              ...dungeon.connections.filter(c => `${c.fromId}-${c.toId}` !== segmentSource.connId),
              conn1, conn2, conn3
            ]
          });
        }
      }
    }
  }, [dungeon, setDungeon]);

  const renderEncounterIcon = (type: EncounterType) => {
    const custom = theme.encounterIcons?.[type] || customEncounters.find(ce => ce.id === type)?.icon;
    if (custom) return <GenericIcon char={custom} size={24} />;
    switch (type) {
      case 'BATTLE': return <X size={26} strokeWidth={4} className="text-red-500" />;
      case 'TRAP': return <ShieldAlert size={26} className="text-purple-400" />;
      case 'TREASURE': return <Gift size={26} className="text-amber-400" />;
      case 'OBSTACLE': return <Ghost size={26} className="text-stone-400" />;
      default: return null;
    }
  };

  return (
    <div
      ref={containerRef}
      className="w-full h-full overflow-hidden cursor-grab active:cursor-grabbing relative"
      style={{ backgroundColor: theme.bg }}
      onPointerDown={handlePointerDown}
      onPointerMove={(e) => handlePointerMove(e.nativeEvent)}
      onPointerUp={handlePointerUp}
      onContextMenu={(e) => e.preventDefault()}
      onDoubleClick={(e) => {
        if (!isMasterMode) return;
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;
        const mouseX = e.clientX - rect.left - viewState.x;
        const mouseY = e.clientY - rect.top - viewState.y;
        const gridX = Math.round(mouseX / (viewState.scale * CELL_SIZE));
        const gridY = Math.round(mouseY / (viewState.scale * CELL_SIZE));

        const exists = dungeon.rooms.some(r => r.gridX === gridX && r.gridY === gridY);
        if (!exists) {
          const newRoom: Room = {
            id: `room-custom-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
            type: 'EMPTY',
            gridX,
            gridY,
            label: 'Nova Sala',
            revealed: false,
            enemies: [],
            description: 'Criada manualmente.'
          };
          setDungeon({ ...dungeon, rooms: [...dungeon.rooms, newRoom] });
        }
      }}
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-20"
        style={{
          backgroundImage: `radial-gradient(${theme.text} 1px, transparent 1px)`,
          backgroundSize: `${CELL_SIZE * viewState.scale}px ${CELL_SIZE * viewState.scale}px`,
          backgroundPosition: `${viewState.x}px ${viewState.y}px`
        }}
      />
      <div
        id="dungeon-inner-canvas"
        className="transition-transform duration-75 ease-out origin-top-left absolute inset-0 pointer-events-none"
        style={{ transform: `translate(${viewState.x}px, ${viewState.y}px) scale(${viewState.scale})` }}
      >
        <div className="relative pointer-events-auto">
          {/* Camada de Corredores Consolidada (Z-Index Inferior) */}
          <div className="relative z-0">
            {(() => {
              // Mapa para consolidar segmentos por coordenadas [gridX, gridY]
              const consolidatedSegments = new Map<string, {
                id: string;
                gridX: number;
                gridY: number;
                encounter: EncounterType;
                revealed: boolean;
                connId: string;
              }>();

              dungeon.connections.forEach(conn => {
                const connId = `${conn.fromId}-${conn.toId}`;
                conn.segments.forEach(seg => {
                  const key = `${seg.gridX},${seg.gridY}`;
                  const isVisible = isMasterMode || seg.revealed;
                  if (!isVisible) return;

                  // Se já existe um segmento nesta célula, mantemos o primeiro (ou priorizamos um com encontro)
                  const existing = consolidatedSegments.get(key);
                  if (!existing || (existing.encounter === 'NONE' && seg.encounter !== 'NONE')) {
                    consolidatedSegments.set(key, { ...seg, connId });
                  }
                });
              });

              const CORRIDOR_SIZE = 34;
              return Array.from(consolidatedSegments.values()).map(seg => (
                <div
                  key={seg.id}
                  onMouseEnter={(e) => isMasterMode && isShiftPressed && setHoveredElement({ x: e.clientX, y: e.clientY, content: `Evento: ${seg.encounter || 'Vazio'}` })}
                  onMouseLeave={() => setHoveredElement(null)}
                  onClick={(e) => {
                    if (isMasterMode && isShiftPressed) {
                      e.stopPropagation();
                      if (!linkSource) {
                        setLinkSource({ id: seg.id, type: 'segment', connId: seg.connId, gridX: seg.gridX, gridY: seg.gridY });
                      } else {
                        handleCreateBifurcation(linkSource, { id: seg.id, type: 'segment', connId: seg.connId, gridX: seg.gridX, gridY: seg.gridY });
                        setLinkSource(null);
                      }
                    }
                  }}
                  onContextMenu={(e) => { e.preventDefault(); if (isMasterMode) onEditSegment(seg.connId, seg.id); }}
                  className={`absolute border flex items-center justify-center transition-all ${seg.revealed ? 'opacity-100' : ''} ${isMasterMode ? (isShiftPressed ? 'cursor-alias' : 'cursor-pointer') : ''} ${linkSource?.id === seg.id ? 'ring-4 ring-white animate-pulse' : ''}`}
                  style={{
                    width: CORRIDOR_SIZE,
                    height: CORRIDOR_SIZE,
                    left: seg.gridX * CELL_SIZE - (CORRIDOR_SIZE / 2),
                    top: seg.gridY * CELL_SIZE - (CORRIDOR_SIZE / 2),
                    backgroundColor: theme.corridor,
                    borderColor: seg.revealed ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)',
                    opacity: seg.revealed ? 1 : theme.masterModeOpacity,
                    borderStyle: seg.revealed ? 'solid' : 'dashed',
                    zIndex: linkSource?.id === seg.id ? 100 : 5
                  }}
                >
                  <div className="flex items-center justify-center scale-75">{renderEncounterIcon(seg.encounter)}</div>
                </div>
              ));
            })()}
          </div>

          {/* Camada de Salas (Z-Index Superior) */}
          <div className="relative z-10">
            {dungeon.rooms.map(room => {
              const isVisible = isMasterMode || room.revealed;
              if (!isVisible) return null;
              return (
                <div
                  key={room.id}
                  onPointerDown={(e) => {
                    if (isMasterMode && e.button === 0) {
                      e.stopPropagation();
                      setDraggingRoomId(room.id);
                    }
                  }}
                  onClick={(e) => {
                    if (isMasterMode && !draggingRoomId) {
                      if (isShiftPressed) {
                        e.stopPropagation();
                        if (!linkSource) {
                          setLinkSource({ id: room.id, type: 'room' });
                        } else if (linkSource.id !== room.id) {
                          if (linkSource.type === 'room') {
                            const from = dungeon.rooms.find(r => r.id === linkSource.id);
                            const to = room;
                            if (from && to) {
                              const rng = new PRNG(Date.now().toString());
                              const newConn = createConnection(from, to, rng, dungeon.rooms);
                              setDungeon({ ...dungeon, connections: [...dungeon.connections, newConn] });
                            }
                          } else {
                            // Link Segment -> Room
                            handleCreateBifurcation(linkSource, { id: room.id, type: 'room' });
                          }
                          setLinkSource(null);
                        } else {
                          setLinkSource(null);
                        }
                      }
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (room.type === 'JUNCTION') return;
                    if (isMasterMode && !isShiftPressed) return;
                    const roomInfo = `Sala: ${room.label}`;
                    const enemiesInfo = room.enemies && room.enemies.length > 0 ? `\n💀 Inimigos: ${room.enemies.join(', ')}` : '';
                    const descInfo = room.description ? `\n📜 ${room.description}` : '';

                    // Lógica de Visibilidade do Tooltip
                    let content = '';
                    if (isMasterMode) {
                      content = `${roomInfo}${enemiesInfo}${descInfo}`;
                    } else if (room.revealed) {
                      // Jogador vê label e descrição, mas talvez não inimigos ocultos (depende da regra, aqui mostrarei tudo se revelado)
                      content = `${roomInfo}${descInfo}`;
                    }

                    if (content) setHoveredElement({ x: e.clientX, y: e.clientY, content });
                  }}
                  onMouseLeave={() => setHoveredElement(null)}
                  onContextMenu={(e) => { e.preventDefault(); if (isMasterMode) onEditRoom(room.id); }}
                  className={`absolute border-4 flex items-center justify-center transition-all group ${isMasterMode ? 'cursor-pointer' : ''}`}
                  style={{
                    width: room.type === 'JUNCTION' ? 1 : CELL_SIZE,
                    height: room.type === 'JUNCTION' ? 1 : CELL_SIZE,
                    left: room.gridX * CELL_SIZE - (room.type === 'JUNCTION' ? 0.5 : CELL_SIZE / 2),
                    top: room.gridY * CELL_SIZE - (room.type === 'JUNCTION' ? 0.5 : CELL_SIZE / 2),
                    backgroundColor: room.type === 'JUNCTION' ? 'transparent' : theme.roomBg,
                    borderColor: 'transparent',
                    opacity: 1,
                    borderStyle: 'none',
                    boxShadow: linkSource?.id === room.id ? `0 0 30px #fff` : (room.revealed && room.type !== 'JUNCTION' ? `0 0 50px rgba(0,0,0,0.8), inset 0 0 20px ${theme.primary}22` : 'none'),
                    transform: linkSource?.id === room.id ? 'scale(1.1)' : 'scale(1)',
                    zIndex: linkSource?.id === room.id ? 50 : (room.type === 'JUNCTION' ? 8 : 10),
                    borderRadius: room.type === 'JUNCTION' ? '50%' : '0'
                  }}
                >
                  {room.type !== 'JUNCTION' && (room.revealed ? renderRoomIcon(room.type) : <Lock size={20} className="text-stone-700" />)}

                  {room.type !== 'JUNCTION' && (
                    <div
                      className="absolute -top-7 whitespace-nowrap text-[9px] uppercase tracking-widest font-bold font-cinzel text-center pointer-events-none w-[180px]"
                      style={{ color: room.revealed ? theme.text : '#444' }}
                    >
                      {room.label}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {isShiftPressed && isMasterMode && hoveredElement && (
        <div
          className="fixed z-[999] bg-black/90 border-2 p-3 text-[10px] uppercase font-cinzel text-white pointer-events-none shadow-2xl backdrop-blur-xl animate-in fade-in zoom-in duration-75"
          style={{ left: hoveredElement.x + 20, top: hoveredElement.y + 20, borderColor: theme.primary }}
        >
          <div className="whitespace-pre-line font-bold mb-1">{hoveredElement.content}</div>
          <div className="text-[8px] text-stone-500 opacity-60">Status da Expedição</div>
        </div>
      )}
    </div>
  );
};

export default DungeonCanvas;
